import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MenuesService } from '@services/menues/menues.service';
import { StorageService } from '@services/shared/storage.service';
import { AuthService } from '@services/auth/auth.service';
import { TiposWebKeys } from '@enum/tiposweb-keys.enum';
import { DiccionarioKeys } from '@enum/idiomas-diccionario-keys.enum';
import { PopUpModalCustomService } from '@services/shared/popup-modal-custom.service';

@Component({
    selector: 'app-menu-edicion',
    templateUrl: './menu-edicion.component.html',
    styleUrls: ['./menu-edicion.component.scss'],
})
export class MenuEdicionComponent implements OnInit, OnDestroy {

    form: FormGroup;
    flagLoaded: boolean = false;
    modoCreacion: boolean = true;
    menuId: string | null = null;
    allMenues: any[] = [];
    nombreTraducido: string = '';
    menuTranslated = new Map<string, string>();

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private fb: FormBuilder,
        private menuesService: MenuesService,
        private storageService: StorageService,
        private authService: AuthService,
        private modalCustom: PopUpModalCustomService,
    ) {
        this.form = this.fb.group({
            name: ['', Validators.required],
            redirectTo: ['', Validators.required],
            tipo: ['WEB', Validators.required],
            icon: [''],
            parent: [null],
            order: [1, [Validators.min(1)]],
        });
    }

    ngOnDestroy(): void {
        this.modalCustom.cerrar();
    }

    async ngOnInit(): Promise<void> {
        this.menuId = this.route.snapshot.queryParamMap.get('id');
        this.modoCreacion = !this.menuId;

        const resp = await this.menuesService.findAllMenues();
        const data = resp?.response ?? resp?.data ?? resp?.docs ?? resp?.results ?? resp;
        this.allMenues = Array.isArray(data) ? data : [];

        await this.cargarNombresTraducidos();

        if (this.menuId) {
            const menuResp: any = await this.menuesService.getItemMenue(this.menuId);
            const menu = menuResp;
            if (menu) {
                const rawName = menu.name || '';
                this.nombreTraducido = this.menuTranslated.get(this.menuId) || this.limpiarNombre(rawName);
                this.form.patchValue({
                    name: rawName,
                    redirectTo: menu.redirectTo || '',
                    tipo: menu.tipo || 'WEB',
                    icon: menu.icon || '',
                    parent: menu.parent || null,
                    order: menu.order || 1,
                });
            }
        }

        this.flagLoaded = true;
    }

    private async cargarNombresTraducidos(): Promise<void> {
        const roots: any[] = await this.menuesService.findRootNodes(
            TiposWebKeys.WEB,
            DiccionarioKeys.I18NFILE,
            'es',
        );

        for (const root of roots) {
            this.menuTranslated.set(root._id, this.limpiarNombre(root.name));
        }

        await Promise.all(roots.map((r) => this.cargarDescendientesRecursivo(r._id)));
    }

    private async cargarDescendientesRecursivo(parentId: string): Promise<void> {
        const children: any[] = await this.menuesService.getFindInmediateDescendants(
            parentId,
            DiccionarioKeys.I18NFILE,
            'es',
        );

        const childPromises = children.map(async (child: any) => {
            if (!this.menuTranslated.has(child._id)) {
                this.menuTranslated.set(child._id, this.limpiarNombre(child.name));
                await this.cargarDescendientesRecursivo(child._id);
            }
        });

        await Promise.all(childPromises);
    }

    get opcionesPadre(): any[] {
        const filtered = this.allMenues.filter((m) => {
            if (this.menuId && m._id === this.menuId) { return false; }
            return true;
        });
        filtered.sort((a, b) => (a.level || 0) - (b.level || 0));
        return filtered;
    }

    indentarMenu(menu: any): string {
        const level = menu.level || 0;
        return '&nbsp;&nbsp;'.repeat(Math.max(0, level - 1));
    }

    limpiarNombre(name: string): string {
        return name.replace(/general-service-signomedico\./g, '');
    }

    nombreTraducidoMenu(menu: any): string {
        return this.menuTranslated.get(menu._id) || this.limpiarNombre(menu.name);
    }

    async guardar(): Promise<void> {
        if (this.form.invalid) { return; }

        const formData = this.form.value;
        const parentId = formData.parent;

        const payload: any = {
            name: formData.name,
            redirectTo: formData.redirectTo,
            tipo: formData.tipo,
            icon: formData.icon || 'string',
            path: 'string',
            item: 'string',
            parent: parentId || null,
            order: formData.order || 1,
        };

        if (parentId) {
            const parentMenu = this.allMenues.find((m) => m._id === parentId);
            payload.level = (parentMenu?.level || 1) + 1;
        } else {
            payload.level = 1;
        }

        let result: any;
        if (this.modoCreacion) {
            result = await this.menuesService.createMenu(payload);
        } else {
            result = await this.menuesService.updateMenu(this.menuId!, payload);
        }

        if (result) {
            const user = await this.authService.getUserLogon();
            this.storageService.clearMenuCache('es', user?.perfil?._id || '');

            this.modalCustom.abrirPopup({
                titulo: this.modoCreacion ? 'Menú creado' : 'Menú actualizado',
                mensaje: this.modoCreacion
                    ? 'El menú se creó correctamente.'
                    : 'El menú se actualizó correctamente.',
                tipo: 'success',
                accionConfirmar: () => this.volverAlListado(),
                accionCerrar: () => this.volverAlListado(),
            });
        } else {
            this.modalCustom.abrirPopup({
                titulo: 'Error al guardar',
                mensaje: 'Ocurrió un error al guardar el menú. Intente nuevamente.',
                tipo: 'error',
                accionConfirmar: () => this.volverAlListado(),
                accionCerrar: () => this.volverAlListado(),
            });
        }
    }

    private volverAlListado(): void {
        this.router.navigateByUrl('/menues-administracion');
    }

    cancelar(): void {
        this.volverAlListado();
    }
}
